/**
 * ============================================================================
 *  File        : frontend/src/data/caseRoutes.ts
 *  Module      : data
 *  Purpose     : spec §8.2 案件子頁路由註冊表（/cases/:id/...）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { PERMISSIONS } from '../lib/permissions'
import type { ModuleStatus } from '../types'

export interface CaseRouteDef {
  /** 相對於 /cases/:id 的子路徑（空字串為總覽）。 */
  path: string
  /** 頁面標題 i18n key。 */
  titleKey: string
  /** spec §8.2 對應描述。 */
  specSection: string
  /** RBAC 權限鍵；null 代表登入即可見。 */
  permissionKey: string | null
  /** 模組狀態：ready / partial / blocked（P3）。 */
  status: ModuleStatus
}

/** spec §8.2 的 13 個案件子頁（不含 /cases 與 /cases/new 兩個頂層）。 */
export const caseRoutes: CaseRouteDef[] = [
  {
    path: '',
    titleKey: 'case.stage.overview',
    specSection: 'spec §8.2 /cases/:id 案件總覽',
    permissionKey: PERMISSIONS.caseRead,
    status: 'partial',
  },
  {
    path: 'items',
    titleKey: 'case.stage.items',
    specSection: 'spec §8.2 /cases/:id/items 品項明細',
    permissionKey: PERMISSIONS.caseManage,
    status: 'partial',
  },
  {
    path: 'shipment',
    titleKey: 'case.stage.shipment',
    specSection: 'spec §8.2 /cases/:id/shipment 出貨資料',
    permissionKey: PERMISSIONS.shipmentManage,
    status: 'ready',
  },
  {
    path: 'receiving',
    titleKey: 'case.stage.receiving',
    specSection: 'spec §8.2 /cases/:id/receiving 荷受け / 重量',
    permissionKey: PERMISSIONS.receivingManage,
    status: 'ready',
  },
  {
    path: 'weight-audit',
    titleKey: 'case.stage.weightAudit',
    specSection: 'spec §8.2 /cases/:id/weight-audit 重量稽核',
    permissionKey: PERMISSIONS.weightVarianceView,
    status: 'ready',
  },
  {
    path: 'settlements/p1',
    titleKey: 'case.stage.p1',
    specSection: 'spec §8.2 /cases/:id/settlements/p1 一回目精算',
    permissionKey: PERMISSIONS.caseRead,
    status: 'ready',
  },
  {
    path: 'auction',
    titleKey: 'case.stage.auction',
    specSection: 'spec §8.2 /cases/:id/auction 入札管理',
    permissionKey: PERMISSIONS.auctionManage,
    status: 'ready',
  },
  {
    path: 'adjustments',
    titleKey: 'case.stage.adjustments',
    specSection: 'spec §8.2 /cases/:id/adjustments 落札結果調整',
    permissionKey: PERMISSIONS.adjustmentPriceAdjust,
    status: 'ready',
  },
  {
    path: 'shares',
    titleKey: 'case.stage.shares',
    specSection: 'spec §8.2 /cases/:id/shares 三方分配（P+Q+R=K）',
    permissionKey: PERMISSIONS.shareManage,
    status: 'ready',
  },
  {
    path: 'settlements/p2',
    titleKey: 'case.stage.p2',
    specSection: 'spec §8.2 /cases/:id/settlements/p2 二回目精算',
    permissionKey: PERMISSIONS.settlementP2,
    status: 'ready',
  },
  {
    path: 'final-weight',
    titleKey: 'case.stage.finalWeight',
    specSection: 'spec §8.2 /cases/:id/final-weight 最終重量',
    permissionKey: PERMISSIONS.finalWeightManage,
    status: 'ready',
  },
  {
    path: 'settlements/p3',
    titleKey: 'case.stage.p3',
    specSection: 'spec §8.2 /cases/:id/settlements/p3 三回目精算（預設 disabled until sign-off）',
    permissionKey: PERMISSIONS.settlementP3,
    status: 'blocked',
  },
  {
    // 最終報告ページは「閲覧」= marketplace.final_report.view でゲート（formal spec §K）。
    // 以前は caseClose（cases.update）でゲートしていたため、三方確認の当事者である
    // メンバー代表 / 会場代表（final_report.view + final_report.confirm は持つが
    // cases.update は持たない）がページ自体に入れず、confirm-member / confirm-venue
    // ボタンに到達できなかった。ページ内の write 操作（review/close は canClose、
    // 三方確認は finalReportConfirm + persona ロール）は引き続き個別にゲートされる。
    path: 'final-report',
    titleKey: 'case.stage.finalReport',
    specSection: 'spec §8.2 /cases/:id/final-report 最終報告 / Closed_Locked',
    permissionKey: PERMISSIONS.finalReportView,
    status: 'ready',
  },
]
